import { Component, Input, OnInit, Output, EventEmitter, ViewChild, AfterViewInit } from '@angular/core';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { RelatedToType } from 'app/shared/enums';
import { getAllowedFields } from 'app/shared/functions';
import { isObjectEqual } from 'app/shared/Utils/common.utils';
import { FieldDef } from '@core/models';

@Component({
  selector: 'select-field',
  templateUrl: './select-field.component.html',
  styleUrls: ['./select-field.component.scss'],
})
export class SelectFieldComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  @ViewChild('select', { static: false }) select: any;

  @Input() model: any = {};
  @Input() fieldDef: any;
  @Input() rowIndex: number;
  @Input() colIndex: number;
  @Input() data: any;
  @Input() type: RelatedToType;
  @Input() readonly: boolean = false;
  @Input() options: Array<any>;
  @Input() labelField: string;
  @Input() emptyModel = false;
  @Input() hasNestedFiltersOn = false;
  @Input() enableEmptyField = false;

  @Output() update: EventEmitter<any> = new EventEmitter();

  private _originalValue: string;
  private _readonly: boolean;
  static readonly emptyFieldDef: FieldDef = {
    label: '--',
    name: '[blank]',
    type: 'text',
  };

  public filteredOptions: Array<FieldDef>;
  public value: string;
  public editing: boolean = false;
  public showTip: string;
  public searchTxt: string;

  protected get emptyFieldDef(): FieldDef {
    return SelectFieldComponent.emptyFieldDef;
  }

  constructor() {
    super();
  }
  ngAfterViewInit(): void {
    this.disableSpaceKeyboardPress();
  }

  ngOnInit() {
    this.value = this.fieldDef.name;

    this._originalValue = this.value;

    if (this.data.fieldDefs?.length > 0) {
      this.options = this.data.fieldDefs;

      if (this.type && !this.hasNestedFiltersOn) {
        this.options = getAllowedFields(this.options, this.type);
      }
    }

    this.filteredOptions = this.options;
  }

  private onSave(): void {
    if (this.emptyModel && this._originalValue != this.value) {
      this.update.emit({ [this.fieldDef.name]: this.value });
      return;
    }

    if (!isObjectEqual(this._originalValue, this.value)) {
      this.updateModel();
    }
  }

  private updateModel(): void {
    if (!Object.keys(this.model).length) {
      return;
    }

    this.model.meta.fields[this.rowIndex][this.colIndex] = this.value;
    this._originalValue = this.value;
    this.update.emit({ [this.fieldDef.name]: this.value });
  }

  /**
   * Prevent the mat-select popup from closing when user hit the Space keyboard while searching for a field definition.
   * Subscribe to openedChange event to keep mat-select scroll always on top.
   */
  private disableSpaceKeyboardPress(): void {
    this.select._handleKeydown = (event: KeyboardEvent) => {
      if (event.code === 'Space') return;
      if (!this.select.disabled)
        this.select.panelOpen ? this.select._handleOpenKeydown(event) : this.select._handleClosedKeydown(event);
    };
    this.select.openedChange.pipe(untilComponentDestroyed(this)).subscribe((isOpen: boolean) => {
      if (isOpen) {
        const panel = this.select.panel.nativeElement as HTMLDivElement;
        panel.scrollTop = 0;
      }
    });
  }

  edit(): void {
    if (!this.editing && !this._readonly) {
      this.editing = true;
    }
  }

  onSelectionChange(): void {
    this.onSave();
  }

  /**
   * When the model is updated after save, re-run our logic
   * @param model
   */
  updateChangedModel(model: any): void {
    this.model = model;
    this.value = this.model.meta.fields[this.rowIndex][this.colIndex];

    if (!isObjectEqual(this._originalValue, this.value)) {
      this._originalValue = this.value;
    }
  }

  onBlur(): void {
    this.onSave();
    this.editing = false;
  }
}
